import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";

import { faBuilding } from "@fortawesome/pro-regular-svg-icons";

import { Icon, LoadingOverlay } from "@/components";

import {
	useLazyGetTrackerOrganizationSignalsQuery,
	useLazyGetProspectSignalsQuery,
} from "@/pages/Private/redux/signal/signal.api";

import { Spinner } from "@/components/Spinner/Spinner";

import { SignalFilterStatus } from "@/enum/list.enum";

import { TimelineEntry } from "../../../schema/trackerSignalResult";

import { ListAppearanceCard } from "./ListAppearanceCard";
import { SignalResultContent } from "./SignalResultContent";

/**
 * A timeline entry's identity for de-duplication across pages: a signal is its own row id, an
 * appearance the `list_person` row it was derived from — the two id spaces are unrelated, so the
 * key has to carry the entry type as well.
 */
const entryKey = (entry: TimelineEntry): string =>
	entry.type === "SIGNAL"
		? `signal-${entry.signal?.id}`
		: `appearance-${entry.listAppearance?.listPersonId}`;

interface SignalTimelineProps {
	trackerOrganizationId?: number;
	prospectId?: number;
	signalFilterStatus?: SignalFilterStatus[];
}

export const SignalTimeline: FunctionComponent<SignalTimelineProps> = ({
	trackerOrganizationId,
	prospectId,
	signalFilterStatus,
}) => {
	const [expandedReasoningIds, setExpandedReasoningIds] = useState<Set<number>>(new Set());
	const [expandedSummaryIds, setExpandedSummaryIds] = useState<Set<number>>(new Set());
	const [allEntries, setAllEntries] = useState<TimelineEntry[]>([]);
	const [page, setPage] = useState(1);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [isShown, setIsShown] = useState(false);
	const signalLimit = 10;

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	const useTrackerOrgQuery = !!trackerOrganizationId;
	const useProspectQuery = !useTrackerOrgQuery && !!prospectId;

	const [fetchTrackerOrgSignals, { isFetching: isFetchingTrackerOrg }] =
		useLazyGetTrackerOrganizationSignalsQuery();
	const [fetchProspectSignals, { isFetching: isFetchingProspect }] =
		useLazyGetProspectSignalsQuery();

	const isFetching = useTrackerOrgQuery ? isFetchingTrackerOrg : isFetchingProspect;

	const toggleReasoningExpanded = (signalId: number) => {
		setExpandedReasoningIds((prev) => {
			const newSet = new Set(prev);

			if (newSet.has(signalId)) {
				newSet.delete(signalId);
			} else {
				newSet.add(signalId);
			}

			return newSet;
		});
	};

	const toggleSummaryExpanded = (signalId: number) => {
		setExpandedSummaryIds((prev) => {
			const newSet = new Set(prev);

			if (newSet.has(signalId)) {
				newSet.delete(signalId);
			} else {
				newSet.add(signalId);
			}

			return newSet;
		});
	};

	// Determine the effective filter to pass to the API
	const effectiveFilter =
		signalFilterStatus &&
		signalFilterStatus.length > 0 &&
		!signalFilterStatus.includes(SignalFilterStatus.ALL)
			? signalFilterStatus
			: undefined;

	// Serialized filter for use in dependency arrays
	const filterKey = effectiveFilter ? JSON.stringify(effectiveFilter) : "";

	// Reset the feed when the filter changes
	useEffect(() => {
		setAllEntries([]);
		setPage(1);
		setHasNextPage(false);
		setIsInitialLoad(true);
	}, [filterKey]);

	// Fetch signals when page changes
	useEffect(() => {
		const fetchSignals = async () => {
			let result;

			if (useTrackerOrgQuery) {
				result = await fetchTrackerOrgSignals({
					trackerOrganizationId: trackerOrganizationId || 0,
					page,
					limit: signalLimit,
					signalFilterStatus: effectiveFilter,
					// This panel is about ONE lead even though its signals are company-wide, so its list
					// appearances must be that lead's own — never a colleague's (PREC-2496).
					appearanceProspectId: prospectId,
				}).unwrap();
			} else if (useProspectQuery) {
				result = await fetchProspectSignals({
					prospectId: prospectId || 0,
					page,
					limit: signalLimit,
					signalFilterStatus: effectiveFilter,
				}).unwrap();
			}

			if (result) {
				setAllEntries((prev) => {
					const existingKeys = new Set(prev.map(entryKey));
					const newEntries = result.data.filter(
						(entry: TimelineEntry) => !existingKeys.has(entryKey(entry))
					);

					return [...prev, ...newEntries];
				});
				setHasNextPage(result.meta.hasNextPage);
			}

			setIsInitialLoad(false);
		};

		fetchSignals();
	}, [page, trackerOrganizationId, prospectId, filterKey]);

	// Lazy load on scroll
	const loadMoreData = useCallback(() => {
		if (!isFetching && hasNextPage) {
			setPage((prev) => prev + 1);
		}
	}, [isFetching, hasNextPage]);

	useEffect(() => {
		if (allEntries.length === 0) {
			return;
		}

		const scrollContainer = scrollContainerRef.current;

		const isLastElementVisible = () => {
			const rect = sentinelRef.current?.getBoundingClientRect();
			const containerRect = scrollContainer?.getBoundingClientRect();

			if (rect && containerRect) {
				const isVisible = rect.bottom <= containerRect.bottom;

				if (isVisible) {
					if (!isShown) {
						setIsShown(true);
						loadMoreData();
					}
				} else {
					setIsShown(false);
				}
			}
		};

		let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

		const handleScroll = () => {
			if (scrollTimeout) {
				clearTimeout(scrollTimeout);
			}

			scrollTimeout = setTimeout(isLastElementVisible, 500);
		};

		if (scrollContainer) {
			scrollContainer.addEventListener("scroll", handleScroll);

			isLastElementVisible();
		}

		return () => {
			scrollContainer?.removeEventListener("scroll", handleScroll);

			if (scrollTimeout) {
				clearTimeout(scrollTimeout);
			}
		};
	}, [allEntries, sentinelRef, page, isShown, loadMoreData]);

	if (isInitialLoad) {
		return (
			<div className="mb-8 flex flex-col">
				<div className="flex items-center justify-center py-8">
					<LoadingOverlay />
				</div>
			</div>
		);
	}

	if (allEntries.length === 0) {
		return (
			<div className="mb-8 flex flex-col">
				<div className="flex flex-col items-center justify-center py-12 text-gray-500">
					<Icon className="w-12 h-12 mb-4 text-gray-300" icon={faBuilding} />
					<p className="text-sm">No signals found</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			<div
				ref={scrollContainerRef}
				className="flex flex-col gap-4 overflow-y-auto max-h-[calc(100vh-540px)] scrollbar-hide"
			>
				{allEntries.map((entry: TimelineEntry) => {
					if (entry.type === "LIST_APPEARANCE") {
						return entry.listAppearance ? (
							<ListAppearanceCard
								key={entryKey(entry)}
								appearance={entry.listAppearance}
								occurredAt={entry.occurredAt}
							/>
						) : null;
					}

					const signal = entry.signal;

					if (!signal) {
						return null;
					}

					return (
						<div
							key={entryKey(entry)}
							className="flex w-full flex-col border border-gray-200 rounded-lg overflow-hidden bg-white p-4 min-h-fit"
						>
							<SignalResultContent
								hidePausedError={true}
								isReasoningExpanded={expandedReasoningIds.has(signal.id)}
								isSummaryExpanded={expandedSummaryIds.has(signal.id)}
								signal={signal}
								onToggleReasoning={toggleReasoningExpanded}
								onToggleSummary={toggleSummaryExpanded}
							/>
						</div>
					);
				})}

				{/* Sentinel for lazy loading */}
				<div ref={sentinelRef} />
			</div>

			{/* Loading spinner while fetching more */}
			{isFetching && hasNextPage && (
				<div className="flex items-center justify-center py-4">
					<Spinner color="#3b82f6" />
				</div>
			)}
		</div>
	);
};
